import {
  AccountTreeOutlined,
  AdminPanelSettingsOutlined,
  BusinessOutlined,
  ChevronRightRounded,
  DescriptionOutlined,
  DownloadOutlined,
  EventAvailableOutlined,
  FactCheckRounded,
  GroupsOutlined,
  RuleOutlined,
  SwapHorizOutlined,
  TuneOutlined,
  VerifiedOutlined,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { createFocusRingStyles, MetricCard, PageScaffold, designTokens } from '../../design-system';
import { HomeServiceCard } from './HomeServiceCard';
import { homeServiceThemes } from './homeServiceThemes';
import type { HomePageKey, HomePageVisibility } from './model';

type DashboardValues = {
  headcount?: string;
  organizations?: string;
  companies?: string;
  pending?: string;
};

type OperationsHomeDashboardProps = {
  values: DashboardValues;
  canAccessInbox: boolean;
  visiblePages: HomePageVisibility;
  onNavigate: (page: HomePageKey) => void;
  onDownload?: () => void;
};

type Service = {
  label: string;
  page: HomePageKey;
  icon: ReactNode;
  accent: string;
  tint: string;
};

const services: readonly Service[] = [
  { label: '구성원', page: 'people', icon: <GroupsOutlined />, ...homeServiceThemes.profile },
  { label: '조직', page: 'organization', icon: <AccountTreeOutlined />, ...homeServiceThemes.directory },
  { label: '인사 발령', page: 'staffing', icon: <SwapHorizOutlined />, ...homeServiceThemes.workflow },
  { label: '온·오프보딩', page: 'lifecycle', icon: <RuleOutlined />, ...homeServiceThemes.tasks },
  { label: '휴가', page: 'absence', icon: <EventAvailableOutlined />, ...homeServiceThemes.absence },
  { label: '채용', page: 'recruiting-home', icon: <WorkOutlineOutlined />, ...homeServiceThemes.recruiting },
  { label: '문서', page: 'documents', icon: <DescriptionOutlined />, ...homeServiceThemes.documents },
  { label: '증명서 설정', page: 'admin-certificates', icon: <VerifiedOutlined />, ...homeServiceThemes.documents },
  { label: '회사 정보', page: 'admin-company-info', icon: <BusinessOutlined />, ...homeServiceThemes.settings },
  { label: '기준 정보', page: 'admin-reference-data', icon: <TuneOutlined />, ...homeServiceThemes.settings },
  { label: '관리자 설정', page: 'admin-security', icon: <AdminPanelSettingsOutlined />, ...homeServiceThemes.settings },
];

export function OperationsHomeDashboard({ values, canAccessInbox, visiblePages, onNavigate, onDownload }: OperationsHomeDashboardProps) {
  const availableServices = services.filter((service) => visiblePages.has(service.page));
  const metrics = [
    values.headcount !== undefined ? { label: '재직 구성원', value: formatMetricValue(values.headcount, '명'), description: '현재 재직 기준', page: 'people' as const } : undefined,
    values.organizations !== undefined ? { label: '등록 조직', value: formatMetricValue(values.organizations, '개'), description: '현재 조회 가능한 조직', page: 'organization' as const } : undefined,
    values.companies !== undefined ? { label: '등록 회사', value: formatMetricValue(values.companies, '개'), description: '현재 등록된 회사', page: 'admin-affiliates' as const } : undefined,
    canAccessInbox && values.pending !== undefined ? { label: '처리 대기 결재', value: formatMetricValue(values.pending, '건'), description: '내가 처리할 현재 단계', page: 'inbox' as const } : undefined,
  ].filter((metric): metric is NonNullable<typeof metric> => Boolean(metric));

  return <Box data-testid="operations-home">
    <PageScaffold
      title="대시보드"
      width="standard"
      prominent
      actions={onDownload ? <ButtonBase aria-label="구성원 내보내기" onClick={onDownload} sx={{ minHeight: 34, px: 1.25, borderRadius: designTokens.radius.button, color: designTokens.color.secondaryText, '&:hover': { bgcolor: designTokens.color.hoverSurface }, '&:focus-visible': focusSx }}>
        <DownloadOutlined sx={{ mr: 0.6, fontSize: 17 }} /><Typography sx={{ ...designTokens.typography.ui, fontWeight: 650 }}>구성원 내보내기</Typography>
      </ButtonBase> : undefined}
    >
      {metrics.length > 0 && <Box component="section" aria-label="운영 현황">
        <Box data-testid="operations-dashboard-metrics" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${Math.min(metrics.length, 4)}, minmax(0, 1fr))` }, gap: 1.5 }}>
          {metrics.map((metric) => <MetricCard
            key={metric.label}
            label={metric.label}
            value={metric.value}
            description={metric.description}
            onClick={visiblePages.has(metric.page) ? () => onNavigate(metric.page) : undefined}
          />)}
        </Box>
      </Box>}

      <Box sx={{ mt: metrics.length > 0 ? `${designTokens.space.xxl}px` : 0 }}>
        {availableServices.length > 0 && <Box component="section" aria-labelledby="services-heading">
          <Typography id="services-heading" component="h2" sx={sectionTitleSx}>서비스</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 2 }}>
            {availableServices.map((service) => <HomeServiceCard key={service.page} {...service} onClick={() => onNavigate(service.page)} />)}
          </Box>
          {canAccessInbox && <ButtonBase onClick={() => onNavigate('inbox')} sx={{ width: '100%', height: 56, mt: 1.5, px: 2, borderRadius: designTokens.radius.surface, justifyContent: 'flex-start', bgcolor: designTokens.color.canvas, color: designTokens.color.secondaryText, '&:hover': { bgcolor: designTokens.color.hoverSurface }, '&:focus-visible': focusSx }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
              <Box sx={{ width: 28, height: 28, border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.circle, display: 'grid', placeItems: 'center', color: designTokens.color.disabledText }}><FactCheckRounded sx={{ fontSize: 16 }} /></Box>
              <Typography sx={{ ...designTokens.typography.ui, fontWeight: 650, letterSpacing: '-0.02em' }}>결재함 바로가기</Typography>
            </Stack>
            <ChevronRightRounded sx={{ fontSize: 20, color: designTokens.color.disabledText }} />
          </ButtonBase>}
        </Box>}

      </Box>
    </PageScaffold>
  </Box>;
}

function formatMetricValue(value: string, unit: string) {
  return value === '…' || value === '—' ? value : `${value}${unit}`;
}

const sectionTitleSx = {
  m: 0,
  ...designTokens.typography.sectionTitle,
  fontWeight: 700,
  color: designTokens.color.primaryText,
  letterSpacing: '-0.04em',
} as const;

const focusSx = createFocusRingStyles({ inset: true });
